import { Namespace, type TFn } from "@racoon/i18n";
import { AI_VERDICTS, isBadScore } from "./ai-verification.verdicts";

// Full class strings rather than interpolated fragments so Tailwind's content
// scanner keeps them.
const PILL_BASE =
  "h-4 w-fit flex items-center px-1.5 py-1.5 rounded-full text-[10px] font-semibold leading-[10px] tracking-tight whitespace-nowrap";
const PILL_BAD = "bg-red-100 text-red-600";
const PILL_NEUTRAL = "bg-slate-200 text-slate-500";

/**
 * The bad verdicts of one photo-recognition card, as red pills.
 *
 * A verdict that came out fine gets no pill for now, and neither does a score that
 * is unset — unset means the check has not run, never 0 and never "fine", so it
 * must not read as either verdict. With nothing bad to show the card falls back to
 * one neutral pill, and that pill distinguishes the two silences: "no issues" only
 * when something was actually scored, "not checked" when nothing was. Thresholds
 * live in ai-verification.verdicts, shared with the verdict filter.
 */
export const AiVerdictBadges = ({ t, verification }: { t: TFn; verification: any }) => {
  const pills = AI_VERDICTS.reduce<{ key: string; label: string }[]>((acc, verdict) => {
    const score = verification?.[verdict.field];
    if (typeof score !== "number" || !isBadScore(verdict, score)) return acc;
    acc.push({ key: verdict.field, label: t(Namespace.Govdash, verdict.badKey as any) });
    return acc;
  }, []);

  if (pills.length === 0) {
    const wasScored = AI_VERDICTS.some(({ field }) => typeof verification?.[field] === "number");
    return (
      <div className="w-[220px] flex flex-col p-4 gap-2.5 border-l">
        <div className={`${PILL_BASE} ${PILL_NEUTRAL}`}>
          {t(
            Namespace.Govdash,
            wasScored ? "aiVerification.noIssues" : "aiVerification.notChecked",
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[220px] flex flex-col p-4 gap-2.5 border-l">
      {pills.map(({ key, label }) => (
        <div key={key} className={`${PILL_BASE} ${PILL_BAD}`}>
          {label}
        </div>
      ))}
    </div>
  );
};
